import React, { useMemo } from 'react';
import { Menu, MenuProps } from 'antd';
import { useLocation, useNavigate } from 'react-router';
import { routes } from '@/router/routes';
import { RouteConfig } from '@/types/route';
import { matchPath } from 'react-router';
import { User } from '@/types/user';

interface MenuPathMapping {
  patterns: string[];
  menuKey: string;
}

const menuPathMappings: MenuPathMapping[] = [
  {
    patterns: ['/admin/employee/:employee_id'],
    menuKey: '/admin/employees',
  },
  {
    patterns: ['/admin/company/:company_id'],
    menuKey: '/admin/companies',
  },
  {
    patterns: ['/admin/enterprise_libraries'],
    menuKey: '/admin/product_libraries',
  },
  {
    patterns: [
      '/admin/configuration/:configuration_id',
      '/admin/product/:configuration_id/:product_id',
      '/admin/bundle/:configuration_id/:bundle_id',
    ],
    menuKey: '/admin/configuration/:configuration_id',
  },
];

const generateMenuItems = (
  routesConfig: RouteConfig[],
  user: User,
  parentPath = ''
): MenuProps['items'] => {
  const items = routesConfig
    .filter(route => !route.hideInMenu)
    .filter(route => {
      if (route.meta?.permissionCheck) {
        return route.meta.permissionCheck(user);
      }
      return true;
    })
    .map(route => {
      // Ensure single slash between parts
      const fullPath = `${parentPath}/${route.path}`.replace(/\/+/g, '/');
      const menuKey = fullPath;
      let navigationPath = fullPath;
      let disabled = false;

      if (route.meta?.getDynamicPath) {
        if (user) {
          const dynamicPath = route.meta.getDynamicPath(user);
          if (dynamicPath) {
            navigationPath = dynamicPath;
          } else {
            disabled = true;
          }
        } else {
          disabled = true;
        }
      }

      return {
        key: menuKey,
        icon: route.meta?.icon,
        label: route.meta?.title,
        disabled,
        ['data-nav-path']: navigationPath,
        children: route.children
          ? generateMenuItems(route.children, user, fullPath)
          : undefined,
      };
    });

  // If there's only one top-level item, return its children instead
  if (items.length === 1 && items[0]?.children) {
    return items[0].children;
  }

  return items;
};

const getDefaultOpenKeys = (pathname: string) => {
  const segments = pathname.split('/').filter(Boolean);
  return segments.map(
    (_, index) => `/${segments.slice(0, index + 1).join('/')}`
  );
};

export const AppMenu: React.FC<{ user: User }> = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = useMemo(() => generateMenuItems(routes, user), [user]);

  const defaultOpenKeys = useMemo(
    () => getDefaultOpenKeys(location.pathname),
    [location.pathname]
  );

  const selectedKey = useMemo(() => {
    const currentPath = location.pathname;

    const mapping = menuPathMappings.find(mapping =>
      mapping.patterns.some(pattern => matchPath(pattern, currentPath))
    );

    if (mapping) {
      return mapping.menuKey;
    }
    return currentPath;
  }, [location.pathname]);

  const handleMenuClick = ({ key, domEvent }: any) => {
    const navPath = domEvent.target
      .closest('[data-nav-path]')
      ?.getAttribute('data-nav-path');

    navigate(navPath || key);
  };

  return (
    <Menu
      mode="inline"
      selectedKeys={[selectedKey]}
      defaultOpenKeys={defaultOpenKeys}
      items={menuItems}
      onClick={handleMenuClick}
      theme="dark"
      className="h-full !border-none bg-[#121212]"
    />
  );
};
